/**
 * GovernmentDepartmentNew — /government/departments/new
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2, Save, Image as ImageIcon } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import GovernmentCategoryNav from "@/components/GovernmentCategoryNav";
import VideoLinkManager from "@/components/VideoLinkManager";

const empty = {
  name: "", level: "", category: "", description: "", responsibilities: "",
  country: "", state: "", district: "", city: "", taluk: "", panchayat_name: "",
  office_address: "", office_hours: "",
  website: "", email: "", phone: "", emergency_phone: "",
  head_official_name: "", logo_url: "", hero_image_url: "", photo_urls: "",
};

export default function GovernmentDepartmentNew() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [videoUrls, setVideoUrls] = useState([]);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { http.get("/government/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!authLoading && !user) { toast.error("Sign in to register."); nav(`/login?redirect=${encodeURIComponent("/government/departments/new")}`, { replace: true }); }
  }, [authLoading, user, nav]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.level) { toast.error("Name and level are required."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        founded_year: undefined,
        photo_urls: form.photo_urls ? form.photo_urls.split(/[\n,]/).map(s => s.trim()).filter(Boolean) : [],
        video_urls: videoUrls,
      };
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      const r = await http.post("/government/departments", payload);
      toast.success("Department registered!");
      nav(`/government/departments/${r.data.department.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create.");
    } finally { setSaving(false); }
  };

  return (
    <>
      <GovernmentCategoryNav />
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="dept-new-page">
        <div className="overline mb-1">NEW DEPARTMENT</div>
        <h1 className="font-display text-4xl mb-2 inline-flex items-center gap-2">
          <Building2 className="w-8 h-8" />Register a government department
        </h1>
        <p className="opacity-70 mb-6">A public directory page — officials, notices, and citizen-message routing included.</p>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Department name *"><input required value={form.name} onChange={e => set("name", e.target.value)} className="wofy-input" data-testid="input-name" /></Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Level *">
              <select required value={form.level} onChange={e => set("level", e.target.value)} className="wofy-input" data-testid="input-level">
                <option value="">Select…</option>
                {meta?.levels?.map(l => <option key={l.slug} value={l.slug}>{l.label}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <select value={form.category} onChange={e => set("category", e.target.value)} className="wofy-input" data-testid="input-category">
                <option value="">Choose…</option>
                {meta?.department_categories?.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Description">
            <textarea rows={4} value={form.description} onChange={e => set("description", e.target.value)} className="wofy-input" data-testid="input-description" />
          </Field>
          <Field label="Responsibilities">
            <textarea rows={3} value={form.responsibilities} onChange={e => set("responsibilities", e.target.value)} className="wofy-input" data-testid="input-responsibilities" />
          </Field>

          <div className="grid gap-3 sm:grid-cols-4">
            <Field label="Country"><input value={form.country} onChange={e => set("country", e.target.value)} className="wofy-input" /></Field>
            <Field label="State"><input value={form.state} onChange={e => set("state", e.target.value)} className="wofy-input" /></Field>
            <Field label="District"><input value={form.district} onChange={e => set("district", e.target.value)} className="wofy-input" /></Field>
            <Field label="City"><input value={form.city} onChange={e => set("city", e.target.value)} className="wofy-input" /></Field>
          </div>

          <Field label="Office address"><input value={form.office_address} onChange={e => set("office_address", e.target.value)} className="wofy-input" /></Field>
          <Field label="Office hours"><input value={form.office_hours} onChange={e => set("office_hours", e.target.value)} placeholder="10 AM – 5 PM (Mon–Fri)" className="wofy-input" /></Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Website"><input value={form.website} onChange={e => set("website", e.target.value)} className="wofy-input" /></Field>
            <Field label="Email"><input type="email" value={form.email} onChange={e => set("email", e.target.value)} className="wofy-input" /></Field>
            <Field label="Phone"><input value={form.phone} onChange={e => set("phone", e.target.value)} className="wofy-input" /></Field>
            <Field label="Emergency number"><input value={form.emergency_phone} onChange={e => set("emergency_phone", e.target.value)} className="wofy-input" /></Field>
            <Field label="Logo URL"><input value={form.logo_url} onChange={e => set("logo_url", e.target.value)} className="wofy-input" /></Field>
            <Field label="Hero image URL"><input value={form.hero_image_url} onChange={e => set("hero_image_url", e.target.value)} className="wofy-input" /></Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2 inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" />Upload photos (URLs, one per line)</div>
              <textarea rows={4} value={form.photo_urls} onChange={e => set("photo_urls", e.target.value)} placeholder="https://…jpg" className="wofy-input font-mono text-xs" data-testid="input-photos" />
            </div>
            <div><VideoLinkManager value={videoUrls} onChange={setVideoUrls} max={6} helper="Paste YouTube or Vimeo links (announcements, press briefings)." /></div>
          </div>

          <div className="pt-2">
            <button disabled={saving} className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Register department
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}
